import { useEffect, useRef, useState } from 'react'
import { QRCodeCanvas } from 'qrcode.react'

import { ANDROID_APK_URL, IOS_APP_STORE_URL } from '../config/appLinks.js'

// ── Token-bound QUALIFYING (TEST) ───────────────────────────────────────────
// Reached from the team QR (/play?t=&tk=) when qualifying is on. The token IS
// the identity — no email, no team picker. Flow:
//   pick teammate → scorecard (hole-by-hole) OR "enter final score only" (a big
//   total box, always tappable) → submit. A manual/final-only card is flagged
//   for pro-shop approval; a full hole-by-hole card is submitted straight.
// User-driven light/dark (matches PlayApp): the per-device choice in localStorage
// drives the theme, falling back to the OS setting until the user picks. Dark
// keeps branding — deep forest → near-black page + dimmed parchment cards.
function readThemeMode() {
  try { const m = localStorage.getItem('pbcc_theme'); return m === 'light' || m === 'dark' ? m : null } catch { return null }
}
const DARK = readThemeMode()
  ? readThemeMode() === 'dark'
  : (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches)
const GREEN = DARK ? '#0B140E' : 'var(--ge-primary)'
const CREAM = DARK ? '#E7E0CE' : 'var(--ge-cream-premium)'
const GOLD = 'var(--ge-gold)'

// Generic token-bound qualifying scorecard. The caller supplies WHO (teamName +
// players), the course pars, the saveable team link, a storage key, and the
// submit fn — so the SAME UI serves both the qualifying-event path and the
// tournament path.
export default function TokenQualify({ teamName, players = [], me = '', pars = [], teamLink = '', storageKey = 'tq', onSubmit, onBack }) {
  const parTotal = (pars || []).reduce((a, b) => a + (Number(b) || 0), 0)
  const KEY = storageKey

  // `me` = the player already chosen on the confirm/landing pick — skip re-asking.
  const [byPlayer, setByPlayer] = useState(me || '')
  const [step, setStep] = useState(me ? 'choose' : 'pick') // pick → choose → card | manual → done
  const [scores, setScores] = useState({}) // { hole: strokes }
  const [sel, setSel] = useState(1)
  const [manualTotal, setManualTotal] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')

  // Restore an in-progress session (survives refresh / auto-update reload).
  useEffect(() => {
    try {
      const s = JSON.parse(localStorage.getItem(KEY) || 'null')
      if (s?.byPlayer) { setByPlayer(s.byPlayer); setStep(s.step || 'card'); setScores(s.scores || {}) }
      else if (me) { setByPlayer(me); setStep('choose') }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [KEY])
  useEffect(() => {
    try { localStorage.setItem(KEY, JSON.stringify({ byPlayer, step, scores })) } catch { /* ignore */ }
  }, [KEY, byPlayer, step, scores])

  const cardTotal = Object.values(scores).reduce((a, b) => a + (Number(b) || 0), 0)
  const filled = Array.from({ length: 18 }, (_, i) => Number(scores[i + 1]) > 0)
  const allFilled = filled.every(Boolean)

  const submit = async ({ manual }) => {
    setBusy(true); setErr('')
    try {
      await onSubmit({
        score: manual ? Number(manualTotal) || 0 : cardTotal,
        holes: manual ? null : scores,
        byPlayer, manual,
      })
      try { localStorage.removeItem(KEY) } catch { /* ignore */ }
      setStep('done')
    } catch (e) {
      setErr(e?.message || 'Could not submit — check your signal and try again.')
    }
    setBusy(false)
  }

  // ── PICK TEAMMATE ─────────────────────────────────────────────────────────
  if (step === 'pick') {
    return (
      <Shell onBack={onBack}>
        <Card>
          <Kicker>Qualifying</Kicker>
          <h1 style={{ color: GREEN, fontSize: 26, fontWeight: 800, margin: '4px 0 2px' }}>{lastNames(teamName)}</h1>
          <p style={{ color: '#5B5B5B', marginBottom: 16 }}>Which player are you?</p>
          {players.map((p) => (
            <button key={p} onClick={() => { setByPlayer(p); setStep('choose') }} style={pickBtn}>{p}</button>
          ))}
          {players.length === 0 ? <p style={{ color: '#B10C0C' }}>No players listed for this team — check with the pro shop.</p> : null}
        </Card>
      </Shell>
    )
  }

  // ── CHOOSE how to enter the score — scorecard OR final total (both obvious) ─
  if (step === 'choose') {
    return (
      <Shell onBack={onBack}>
        <Card>
          <Kicker>Qualifying · {firstName(byPlayer)}</Kicker>
          <h1 style={{ color: GREEN, fontSize: 24, fontWeight: 800, margin: '4px 0 2px' }}>{lastNames(teamName)}</h1>
          <p style={{ color: '#5B5B5B', margin: '4px 0 16px' }}>How do you want to enter your score?</p>
          <button onClick={() => setStep('card')} style={chooseBtn}>
            <span style={{ fontSize: 26 }}>📝</span>
            <span>
              <span style={{ display: 'block', fontWeight: 800, fontSize: 17 }}>Hole-by-hole scorecard</span>
              <span style={{ display: 'block', fontSize: 13, color: '#5B5B5B', fontWeight: 600 }}>Tap in each hole as you play</span>
            </span>
          </button>
          <button onClick={() => setStep('manual')} style={chooseBtn}>
            <span style={{ fontSize: 26 }}>🔢</span>
            <span>
              <span style={{ display: 'block', fontWeight: 800, fontSize: 17 }}>Enter final total only</span>
              <span style={{ display: 'block', fontSize: 13, color: '#5B5B5B', fontWeight: 600 }}>Just your team's total for the round</span>
            </span>
          </button>
        </Card>
      </Shell>
    )
  }

  // ── DONE — score in; offer BOTH save options, player's choice ─────────────
  if (step === 'done') {
    return (
      <Shell onBack={onBack}>
        <Card>
          <div style={{ fontSize: 38, textAlign: 'center' }}>✅</div>
          <h1 style={{ color: GREEN, fontSize: 22, fontWeight: 800, textAlign: 'center', margin: '4px 0 2px' }}>Score submitted</h1>
          <p style={{ color: '#5B5B5B', textAlign: 'center' }}>{lastNames(teamName)} — thanks, {firstName(byPlayer)}. The pro shop will confirm your score.</p>
          <p style={{ color: GREEN, textAlign: 'center', fontWeight: 700, fontSize: 13.5, margin: '12px 0 2px' }}>Save your team so you can reopen anytime — pick either:</p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8, alignItems: 'stretch' }}>
            <SaveOption title="① Save to Photos">
              <TeamQrImage url={teamLink} size={172} />
              <p style={optText}>Press and hold this code → <b>Save to Photos</b>, then tap it from Photos anytime to open as your team.</p>
            </SaveOption>
            <SaveOption title="② Add to Home Screen">
              <InstallBlock />
            </SaveOption>
          </div>
          <p style={{ color: '#8C8C8C', fontSize: 11.5, textAlign: 'center', marginTop: 12 }}>Both reopen the app as your team — no re-scan.</p>
        </Card>
      </Shell>
    )
  }

  // ── MANUAL (final score only) ─────────────────────────────────────────────
  if (step === 'manual') {
    const n = Number(manualTotal)
    return (
      <Shell onBack={onBack}>
        <Card>
          <Kicker>Final score</Kicker>
          <h1 style={{ color: GREEN, fontSize: 22, fontWeight: 800, margin: '4px 0 2px' }}>{lastNames(teamName)}</h1>
          <p style={{ color: '#5B5B5B', marginBottom: 14 }}>Enter your team's total for the round.</p>
          <input
            inputMode="numeric" pattern="[0-9]*" autoFocus
            value={manualTotal}
            onChange={(e) => setManualTotal(e.target.value.replace(/[^0-9]/g, '').slice(0, 3))}
            placeholder="00"
            style={bigBox}
          />
          <p style={{ color: '#8C8C8C', textAlign: 'center', fontSize: 13, margin: '6px 0 16px' }}>Par {parTotal}</p>
          {err ? <p style={{ color: '#B10C0C', marginBottom: 10 }}>{err}</p> : null}
          <button disabled={!(n > 0) || busy} onClick={() => submit({ manual: true })} style={{ ...primaryBtn, opacity: n > 0 && !busy ? 1 : 0.5 }}>
            {busy ? 'Submitting…' : 'Submit final score'}
          </button>
          <button onClick={() => setStep('card')} style={linkBtn}>← Back to scorecard</button>
        </Card>
      </Shell>
    )
  }

  // ── SCORECARD (hole-by-hole) ──────────────────────────────────────────────
  return (
    <Shell onBack={onBack}>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <div>
            <Kicker>Qualifying · {firstName(byPlayer)}</Kicker>
            <h1 style={{ color: GREEN, fontSize: 20, fontWeight: 800, margin: '2px 0' }}>{lastNames(teamName)}</h1>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: GREEN, fontSize: 28, fontWeight: 800 }}>{cardTotal || '—'}</div>
            <div style={{ color: '#8C8C8C', fontSize: 11 }}>{filled.filter(Boolean).length}/18 · par {parTotal}</div>
          </div>
        </div>
        <div style={banner}>Use the scorecard, or enter only your score at the end of the round.</div>

        {/* Hole grid */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(9, 1fr)', gap: 5, marginTop: 12 }}>
          {Array.from({ length: 18 }, (_, i) => i + 1).map((h) => (
            <button key={h} onClick={() => setSel(h)} style={holeCell(sel === h, Number(scores[h]) > 0)}>
              <div style={{ fontSize: 9, color: sel === h ? CREAM : '#8C8C8C' }}>{h}</div>
              <div style={{ fontSize: 16, fontWeight: 800, color: sel === h ? CREAM : Number(scores[h]) > 0 ? GREEN : '#C9C4B6' }}>{scores[h] || '·'}</div>
            </button>
          ))}
        </div>

        {/* Number pad for the selected hole */}
        <div style={{ marginTop: 10, color: '#8C8C8C', fontSize: 12 }}>Hole {sel} · par {pars[sel - 1] || 4}</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 6, marginTop: 6 }}>
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
            <button key={n} onClick={() => { setScores((s) => ({ ...s, [sel]: n })); setSel((h) => Math.min(18, h + 1)) }} style={padBtn}>{n}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <button onClick={() => setScores((s) => { const c = { ...s }; delete c[sel]; return c })} style={{ ...padBtn, flex: 1 }}>Clear hole</button>
          <button onClick={() => setSel((h) => Math.max(1, h - 1))} style={{ ...padBtn, flex: 1 }}>‹ Prev</button>
          <button onClick={() => setSel((h) => Math.min(18, h + 1))} style={{ ...padBtn, flex: 1 }}>Next ›</button>
        </div>

        {err ? <p style={{ color: '#B10C0C', marginTop: 10 }}>{err}</p> : null}
        <button disabled={!allFilled || busy} onClick={() => submit({ manual: false })} style={{ ...primaryBtn, marginTop: 14, opacity: allFilled && !busy ? 1 : 0.5 }}>
          {busy ? 'Submitting…' : allFilled ? `Submit scorecard (${cardTotal})` : `Submit scorecard — ${filled.filter(Boolean).length}/18 holes`}
        </button>

        {/* Always-available final-score-only path */}
        <button onClick={() => setStep('manual')} style={finalOnlyBtn}>Enter final score only →</button>
        {/* Progress auto-saves; this just exits — reopen to resume where you left off. */}
        {onBack ? <button onClick={onBack} style={linkBtn}>⛈ Rain Delay — pause &amp; resume later</button> : null}
      </Card>
    </Shell>
  )
}

// ── bits ────────────────────────────────────────────────────────────────────
// The QR is rendered to a hidden <canvas>, then shown as a PNG <img> — iOS
// Safari's "press and hold → Save to Photos" only appears on an <img>, not a
// <canvas>. Falls back to the canvas until the data URL is ready.
export function TeamQrImage({ url, size }) {
  const canvasRef = useRef(null)
  const [dataUrl, setDataUrl] = useState('')
  useEffect(() => {
    const c = canvasRef.current
    if (c && typeof c.toDataURL === 'function') {
      try { setDataUrl(c.toDataURL('image/png')) } catch { /* ignore */ }
    }
  }, [url, size])
  return (
    <>
      <div style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden', opacity: 0, pointerEvents: 'none' }} aria-hidden="true">
        <QRCodeCanvas ref={canvasRef} value={url} size={size} includeMargin level="M" />
      </div>
      {dataUrl
        ? <img src={dataUrl} width={size} height={size} alt="Your team QR code" style={{ width: size, height: size, display: 'block', margin: '0 auto', borderRadius: 10 }} />
        : <div style={{ display: 'flex', justifyContent: 'center' }}><QRCodeCanvas value={url} size={size} includeMargin level="M" /></div>}
    </>
  )
}

const optText = { color: GREEN, fontSize: 12.5, lineHeight: 1.45, fontWeight: 600, textAlign: 'center', marginTop: 10 }

// One of the two save choices on the finish screen (side by side when they fit,
// stacked on a narrow phone).
function SaveOption({ title, children }) {
  return (
    <div style={{ flex: '1 1 190px', minWidth: 168, background: '#FFFFFF', border: `1px solid ${GOLD}66`, borderRadius: 16, padding: 14, display: 'flex', flexDirection: 'column' }}>
      <div style={{ color: GOLD, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', fontSize: 10.5, textAlign: 'center', marginBottom: 10 }}>{title}</div>
      {children}
    </div>
  )
}

// Get-the-app option: iPhone → App Store (when the store link is live; otherwise
// Add-to-Home-Screen), Android → direct APK download. Also offers the one-tap PWA
// install when the browser fires beforeinstallprompt (captured in main.jsx).
function InstallBlock() {
  const [canPrompt, setCanPrompt] = useState(typeof window !== 'undefined' && !!window.__pbccInstallPrompt)
  useEffect(() => {
    const on = () => setCanPrompt(true)
    window.addEventListener('pbcc-install-available', on)
    return () => window.removeEventListener('pbcc-install-available', on)
  }, [])
  const isIOS = typeof window !== 'undefined' && /iphone|ipad|ipod/i.test(window.navigator.userAgent)
  const install = async () => {
    const p = window.__pbccInstallPrompt
    if (!p) return
    try { p.prompt(); await p.userChoice } catch { /* ignore */ }
    window.__pbccInstallPrompt = null
  }
  const btn = { width: '100%', display: 'block', boxSizing: 'border-box', background: GREEN, color: '#fff', fontWeight: 800, fontSize: 15, padding: '13px', borderRadius: 12, border: 'none', textAlign: 'center', textDecoration: 'none' }
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 8 }}>
      {isIOS ? (
        IOS_APP_STORE_URL ? (
          <a href={IOS_APP_STORE_URL} style={btn}> Get it on the App Store</a>
        ) : (
          <p style={optText}>The iPhone app is coming to the App Store. For now, tap the <b>Share</b> button at the bottom of Safari, then <b>Add to Home Screen</b>.</p>
        )
      ) : (
        <>
          {ANDROID_APK_URL ? <a href={ANDROID_APK_URL} style={btn}>⤓ Download the Android app (APK)</a> : null}
          {canPrompt ? (
            <button onClick={install} style={{ ...btn, background: 'transparent', color: GREEN, border: `1.5px solid ${GREEN}` }}>Or install as a web app</button>
          ) : !ANDROID_APK_URL ? (
            <p style={optText}>Open your browser menu (⋮), then <b>Add to Home screen</b> / <b>Install app</b>.</p>
          ) : null}
        </>
      )}
      <p style={{ ...optText, color: '#5B5B5B', marginTop: 4 }}>Drops a <b>PBCC 4Ball</b> icon that reopens bound to your team.</p>
    </div>
  )
}

function Shell({ children, onBack }) {
  return <div style={{ minHeight: '100%', background: GREEN, padding: '18px 14px 40px' }}><div style={{ maxWidth: 460, margin: '0 auto' }}>
    {onBack ? <button onClick={onBack} style={{ background: 'transparent', border: 'none', color: CREAM, opacity: 0.85, fontWeight: 700, fontSize: 14, padding: '0 0 10px', cursor: 'pointer' }}>← Team home</button> : null}
    {children}
  </div></div>
}
function Card({ children }) {
  return <div style={{ background: CREAM, border: `1px solid ${GOLD}55`, borderRadius: 20, padding: 18, boxShadow: '0 10px 30px rgba(0,0,0,.25)' }}>{children}</div>
}
function Kicker({ children }) {
  return <div style={{ color: GOLD, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.18em', fontSize: 11 }}>{children}</div>
}
function lastNames(name) {
  return String(name || '').split('/').map((p) => p.trim().split(/\s+/).pop()).filter(Boolean).join(' / ')
}
function firstName(name) { return String(name || '').trim().split(/\s+/)[0] || 'player' }

const pickBtn = { display: 'block', width: '100%', textAlign: 'left', background: '#FFFFFF', border: `1px solid ${GREEN}33`, color: GREEN, fontWeight: 700, fontSize: 18, padding: '16px 18px', borderRadius: 14, marginBottom: 10 }
const chooseBtn = { display: 'flex', alignItems: 'center', gap: 14, width: '100%', textAlign: 'left', background: '#FFFFFF', border: `1.5px solid ${GREEN}44`, color: GREEN, padding: '16px 18px', borderRadius: 14, marginBottom: 12, cursor: 'pointer' }
const banner = { marginTop: 12, background: `${GOLD}22`, border: `1px solid ${GOLD}66`, color: GREEN, borderRadius: 12, padding: '10px 12px', fontSize: 13, fontWeight: 600 }
const primaryBtn = { width: '100%', background: GREEN, color: '#FFFFFF', fontWeight: 800, fontSize: 17, padding: '15px', borderRadius: 14, border: 'none' }
const finalOnlyBtn = { width: '100%', marginTop: 10, background: 'transparent', color: GREEN, fontWeight: 800, fontSize: 15, padding: '12px', borderRadius: 12, border: `1.5px solid ${GREEN}` }
const linkBtn = { width: '100%', marginTop: 10, background: 'transparent', color: '#5B5B5B', fontWeight: 700, fontSize: 14, padding: '10px', border: 'none' }
const bigBox = { width: '100%', textAlign: 'center', fontSize: 64, fontWeight: 800, color: GREEN, background: '#FFFFFF', border: `2px solid ${GREEN}44`, borderRadius: 18, padding: '18px 0', letterSpacing: '0.05em' }
const padBtn = { background: '#FFFFFF', border: `1px solid ${GREEN}22`, color: GREEN, fontWeight: 800, fontSize: 18, padding: '12px 0', borderRadius: 10 }
function holeCell(active, filled) {
  return { background: active ? GREEN : '#FFFFFF', border: `1px solid ${active ? GREEN : filled ? `${GREEN}44` : '#E5E0D2'}`, borderRadius: 8, padding: '5px 0' }
}
